import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trash2, Loader2, EyeOff, Link2, Plus, Landmark } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import PlaidConnectButton from "./PlaidConnectButton";
import { fmtMoney, fmtShortDate, cardCls, inputCls, labelCls } from "./accountingUtils";

const STATUS_TABS = [["new", "To review"], ["matched", "Matched"], ["ignored", "Ignored"]];

/**
 * AccountingBanking — connected bank accounts (Plaid) + the "review bank
 * transactions" flow: add as expense, match to an existing expense, ignore.
 */
const AccountingBanking = ({ base, scope, readOnly, viewUserId, items, onItemsChanged, expenses, onAddExpenseFromTx, onExpensesChanged }) => {
  const confirm = useConfirm();
  const [status, setStatus] = useState("new");
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(null);
  const [matchFor, setMatchFor] = useState(null); // tx being matched

  const loadTxs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/transactions`, { params: { status, user_id: viewUserId || undefined } });
      setTxs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load transactions");
    } finally {
      setLoading(false);
    }
  }, [base, status, viewUserId]);

  useEffect(() => { loadTxs(); }, [loadTxs]);

  const sync = async (item) => {
    setWorking(item.item_id);
    try {
      const { data } = await api.post(`${base}/items/${item.item_id}/sync`);
      await api.post(`${base}/items/${item.item_id}/balance`).catch(() => {});
      toast.success(`${item.institution_name}: ${data.added} new transaction${data.added === 1 ? "" : "s"}`);
      onItemsChanged?.();
      loadTxs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setWorking(null);
    }
  };

  const disconnect = async (item) => {
    const ok = await confirm({ title: `Disconnect ${item.institution_name}?`, description: "Unreviewed transactions from this bank are removed. Expenses you already created stay.", confirmLabel: "Disconnect" });
    if (!ok) return;
    try {
      await api.delete(`${base}/items/${item.item_id}`);
      toast.success("Bank disconnected");
      onItemsChanged?.();
      loadTxs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    }
  };

  const toggleDeposit = async (item, acct, v) => {
    try {
      await api.put(`/banking/personal/items/${item.item_id}/accounts/${acct.account_id}`, { allow_direct_deposit: v });
      toast.success(v ? "The company can now direct-deposit to this account" : "Direct deposit to this account turned off");
      onItemsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const ignore = async (tx) => {
    try {
      await api.post(`${base}/transactions/${tx.transaction_id}/ignore`);
      loadTxs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const match = async (tx, expense_id) => {
    try {
      await api.post(`${base}/transactions/${tx.transaction_id}/match`, { expense_id });
      toast.success("Matched to expense");
      setMatchFor(null);
      loadTxs();
      onExpensesChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not match");
    }
  };

  const candidates = (tx) => {
    const amt = Math.abs(tx.amount);
    return (expenses || [])
      .filter((e) => !e.bank_transaction_id)
      .map((e) => ({ e, d: Math.abs((e.total || 0) - amt) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 8);
  };

  return (
    <div className="space-y-4" data-testid="acct-banking">
      {/* Connected banks */}
      <div className={cardCls}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-white">Connected banks</div>
            <p className="mt-0.5 text-xs text-white/50">{scope === "company" ? "The company's accounts." : "Your own accounts — only you can see them."} Transactions pull in automatically; review them below.</p>
          </div>
          {!readOnly && <PlaidConnectButton base={base} onConnected={() => { onItemsChanged?.(); loadTxs(); }} />}
        </div>
        {items.length === 0 ? (
          <div className="mt-4 flex items-center gap-3 text-white/40" data-testid="acct-banking-empty"><Landmark className="h-5 w-5" /><span className="text-xs">No bank connected yet.</span></div>
        ) : (
          <ul className="mt-4 space-y-3">
            {items.map((it) => (
              <li key={it.item_id} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`bank-item-${it.item_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{it.institution_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{it.last_synced_at ? `synced ${fmtShortDate(it.last_synced_at)}` : "waiting for first sync"}</div>
                  </div>
                  {!readOnly && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button type="button" onClick={() => sync(it)} disabled={working === it.item_id} data-testid={`bank-item-${it.item_id}-sync`} aria-label="Sync" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white disabled:opacity-50">
                        {working === it.item_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      </button>
                      <button type="button" onClick={() => disconnect(it)} data-testid={`bank-item-${it.item_id}-remove`} aria-label="Disconnect" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
                <ul className="mt-2 divide-y divide-white/10">
                  {(it.accounts || []).map((a) => (
                    <li key={a.account_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="text-white/80">{a.name || a.subtype} <span className="font-mono text-white/40">••{a.mask}</span></span>
                      <span className="font-mono text-white">{a.balance_current != null ? fmtMoney(a.balance_current) : "—"}</span>
                      {scope === "personal" && !readOnly && (
                        <label className="flex w-full items-center gap-2 text-xs text-white/60">
                          <input type="checkbox" checked={!!a.allow_direct_deposit} onChange={(e) => toggleDeposit(it, a, e.target.checked)} data-testid={`bank-acct-${a.account_id}-deposit`} className="h-3.5 w-3.5 accent-[#1a3fd1]" />
                          Allow the company to direct-deposit pay into this account
                        </label>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Transactions review */}
      <div className="space-y-3">
        <div className="flex gap-1 rounded-md border border-white/10 bg-white/[0.02] p-1" role="tablist">
          {STATUS_TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`bank-tx-tab-${k}`} className={`flex-1 rounded px-2 py-1.5 font-mono text-[11px] uppercase tracking-widest ${status === k ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : txs.length === 0 ? (
          <div className={`${cardCls} py-8 text-center text-sm text-white/50`} data-testid="bank-tx-empty">{status === "new" ? "Nothing to review." : `No ${status} transactions.`}</div>
        ) : (
          <ul className="divide-y divide-white/10 overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]">
            {txs.map((tx) => (
              <li key={tx.transaction_id} className="px-4 py-3" data-testid={`bank-tx-${tx.transaction_id}`}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{fmtShortDate(tx.date)}{tx.pending ? " · pending" : ""}</div>
                    <div className="truncate text-sm font-semibold text-white">{tx.name}</div>
                    <div className="truncate text-xs text-white/55">{tx.category || "Uncategorized"}</div>
                  </div>
                  <span className={`font-mono text-sm font-semibold ${tx.amount < 0 ? "text-emerald-300" : "text-white"}`}>{tx.amount < 0 ? "+" : ""}{fmtMoney(Math.abs(tx.amount))}</span>
                </div>
                {status === "new" && !readOnly && tx.amount > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" onClick={() => onAddExpenseFromTx(tx)} data-testid={`bank-tx-${tx.transaction_id}-add`} className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[#1a3fd1]/85"><Plus className="h-3 w-3" /> Add as expense</button>
                    <button type="button" onClick={() => setMatchFor(matchFor?.transaction_id === tx.transaction_id ? null : tx)} data-testid={`bank-tx-${tx.transaction_id}-match`} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] text-white/80 hover:border-white/25"><Link2 className="h-3 w-3" /> Match</button>
                    <button type="button" onClick={() => ignore(tx)} data-testid={`bank-tx-${tx.transaction_id}-ignore`} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] text-white/80 hover:border-white/25"><EyeOff className="h-3 w-3" /> Ignore</button>
                  </div>
                )}
                {status === "new" && !readOnly && tx.amount < 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-[11px] text-white/40">Money in — income tracking comes next.</span>
                    <button type="button" onClick={() => ignore(tx)} data-testid={`bank-tx-${tx.transaction_id}-ignore`} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] text-white/80 hover:border-white/25"><EyeOff className="h-3 w-3" /> Ignore</button>
                  </div>
                )}
                {status === "ignored" && !readOnly && (
                  <button type="button" onClick={() => ignore(tx)} data-testid={`bank-tx-${tx.transaction_id}-restore`} className="mt-2 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] text-white/80 hover:border-white/25">Move back to review</button>
                )}
                {matchFor?.transaction_id === tx.transaction_id && (
                  <div className="mt-2 rounded-md border border-white/10 bg-white/[0.03] p-2">
                    <label className={labelCls}>Match to an existing expense</label>
                    <select defaultValue="" onChange={(e) => e.target.value && match(tx, e.target.value)} data-testid={`bank-tx-${tx.transaction_id}-match-select`} className={`mt-1 ${inputCls} bg-[#0a0a0a]`}>
                      <option value="">Pick an expense…</option>
                      {candidates(tx).map(({ e }) => <option key={e.expense_id} value={e.expense_id}>{fmtShortDate(e.date)} · {e.payee || e.category} · {fmtMoney(e.total)}</option>)}
                    </select>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AccountingBanking;
